import { FormControl } from '../../../../../../widgets/formControl';
import React, { FC, useCallback } from 'react';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { ascComparator, T } from 'src/shared/helpers';
import PhotoIcon from 'src/shared/material_icons/image/photo_camera.svg';
import theme from 'src/shared/theme';
import styled from 'styled-components';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import { useTreeNodeManager } from '../../scene-editors/hooks/useTreeNodeManager';
import ComposerPreview from '../composerPreview';
import ComposerActions from './composerActions';
import { ComposerAttributeOptionNode, ComposerAttributeType, ComposerOptionActionNode } from './composerAttributesModels';
import ComposerOptions from './composerOptions';
import Stack from '../../../../../../widgets/stack/stack';

const ComposerAttributesContent = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row;
    overflow: hidden;
`;

const ComposerSpecialContent = styled.div`
    width: 63%;
    border-right: 1px solid ${theme.app.borderColor};
    display: flex;
    justify-content: center;
    align-items: flex-start;
    background-color: #f6f8f9;
`;

const ComposerActionsPlaceholder = styled.div`
    width: 38%;
    border-right: 1px solid ${theme.app.borderColor};
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding-top: 24px;
    background-color: #f6f8f9;
`;

const ComposerGroupNodeContent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    padding-top: 2rem;
    gap: 20px;
`;

const ComposerPhotoBlock = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: end;
    width: 96px;
    height: 96px;
    background-color: ${theme.app.backgroundColorBase};
    border: 1px solid ${theme.app.borderColor};
    overflow: hidden;
    position: relative;
    & > img,
    svg {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const ComposerGroupNodeElement = styled(FormControl)`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;

    & > label {
        font-weight: bold;
        font-size: 1.2rem;
    }
`;

const EMPTY_OPTIONS: ComposerAttributeOptionNode[] = [];
const EMPTY_ACTIONS: ComposerOptionActionNode[] = [];

const ComposerAttributeContentPage: FC<{}> = () => {
    const [attributes, selectedAttributes, selectedOptions, storeMeshList, storeMaterialsList, storeCameraList] = useComposerStore((state) => [
        state.attributes,
        state.selectedAttributes,
        state.selectedOptions,
        state.sceneObjectList,
        state.sceneMaterialList,
        state.sceneCameraList
    ]);

    const [zkScene, sceneEditor, zkProduct] = usePreview3DStore((x) => [x.zkScene, x.sceneEditor, x.zkProduct]);
    const mutipleAttributesSelected = (selectedAttributes?.length ?? 0) != 1;

    const multipleOptionsSelected = (selectedOptions?.length ?? 0) != 1;

    const cameraList = React.useMemo(() => {
        if (!zkScene) return [];
        return zkScene.get('cameraLocations')?.models?.sort(ascComparator((item) => item.get('name') ?? '')) ?? [];
    }, [zkScene]);
    const meshList = storeMeshList.filter((x) => sceneEditor!.isRealMesh(x)).sort(ascComparator((item) => item.name ?? '')) ?? [];
    const materialsList =
        storeMaterialsList.filter((x) => x.id != 'default material' && x.name != 'default material').sort(ascComparator((item) => item.name ?? '')) ?? [];
    const camerasList =
        storeCameraList.filter((x) => x.id != 'default camera' && x.name != 'default camera').sort(ascComparator((item) => item.name ?? '')) ?? [];
    const selectedAttribute = React.useMemo(() => selectedAttributes?.[0] ?? null, [selectedAttributes]);
    const createNodeOverlay = useCallback((node: ComposerAttributeOptionNode) => {
        return () => null;
    }, []);
    const createActionNodeOverlay = useCallback((node: ComposerOptionActionNode) => {
        return () => null;
    }, []);

    const options = selectedAttribute?.options ?? EMPTY_OPTIONS;
    const actions = options ? (selectedOptions?.[0]?.actions ?? EMPTY_ACTIONS) : EMPTY_ACTIONS;

    const optionTreeState = useTreeNodeManager<ComposerAttributeOptionNode>(options, createNodeOverlay);
    const actionsTreeState = useTreeNodeManager<ComposerOptionActionNode>(actions, createActionNodeOverlay);
    return (
        <ComposerAttributesContent>
            {selectedAttribute && selectedAttribute!.type === ComposerAttributeType.Attribute && !mutipleAttributesSelected ? (
                <>
                    <ComposerOptions selectedAttribute={selectedAttribute} options={options} multiple={mutipleAttributesSelected} state={optionTreeState} />
                    {actions && !multipleOptionsSelected ? (
                        <ComposerActions
                            selectedAttribute={selectedAttribute}
                            selectedOption={selectedOptions?.[0]}
                            actions={actions}
                            multiple={multipleOptionsSelected}
                            state={actionsTreeState}
                            materials={materialsList}
                            meshes={meshList}
                            cameras={camerasList}
                        />
                    ) : (
                        <ComposerActionsPlaceholder>
                            <>{T._('Select a single element to edit it', 'Admin')}</>
                        </ComposerActionsPlaceholder>
                    )}
                </>
            ) : (
                <ComposerSpecialContent>
                    <ComposerGroupNodeContent>
                        {selectedAttributes?.length === 1 && selectedAttribute ? (
                            <>
                                {selectedAttribute.type === ComposerAttributeType.AttributeGroup && (
                                    <ComposerGroupNodeElement>
                                        <ComposerPhotoBlock>
                                            {!selectedAttribute.img && <PhotoIcon />}
                                            {selectedAttribute.img && <img src={selectedAttribute.img} alt={selectedAttribute.name} />}
                                            {!selectedAttribute.img && <div>{T._('No image', 'Admin')}</div>}
                                        </ComposerPhotoBlock>
                                    </ComposerGroupNodeElement>
                                )}
                                <Stack>
                                    <ComposerGroupNodeElement
                                        label={T._(selectedAttribute.type === ComposerAttributeType.AttributeStep ? 'Step' : 'Group' + ' Name', 'Admin')}
                                    >
                                        {selectedAttribute.name}
                                    </ComposerGroupNodeElement>
                                    <ComposerGroupNodeElement label={T._('Camera position', 'Admin')}>
                                        {cameraList
                                            ?.filter((camera) => camera.get('locationID') === selectedAttribute.camera)
                                            .map((camera) => camera.get('name'))
                                            .join(', ') || T._('No camera position assigned', 'Admin')}
                                    </ComposerGroupNodeElement>
                                    <ComposerGroupNodeElement label={T._('Objects', 'Admin')}>
                                        {selectedAttribute.objects
                                            ?.split(';')
                                            .map((object) => meshList.find((mesh) => mesh.name === object)?.name)
                                            .filter((name) => name !== undefined)
                                            .join(', ') || T._('No objects assigned', 'Admin')}
                                    </ComposerGroupNodeElement>
                                    <ComposerGroupNodeElement label={T._('Attributes', 'Admin')}>
                                        {attributes
                                            .filter((attr) => attr.parentID === selectedAttribute.id)
                                            .map((attr) => attr.name)
                                            .join(', ') || T._('No attributes assigned', 'Admin')}
                                    </ComposerGroupNodeElement>
                                </Stack>
                            </>
                        ) : (
                            <>{T._('Select a single element to edit it', 'Admin')}</>
                        )}
                    </ComposerGroupNodeContent>
                </ComposerSpecialContent>
            )}
            <ComposerPreview />
        </ComposerAttributesContent>
    );
};

export default ComposerAttributeContentPage;
